// Resource Manager
//
// Uses data created by ReflexLoader to create objects for use in game
// Resource Manager handles orientation changes, laying out the game in the new orientation.
// Objects to be created are defined in layout.json. Base types as follows:
//
//  - Sprite
//  - Button
//  - Movie
//  - Reel
//  - Meter
//  - Text
//  - BitmapText
//
// Additional object types can be used with "registerObjectType", passing the name and creation function.
// When objects have all been created use the following to get access to the object:
//
//  resourceManager.get(objectName)
//  resourceManager.getContainer(containerName)
//

resourceManager = {};
resourceManager.resources = [];
resourceManager.generatedResources = {};
resourceManager.containers = {};
resourceManager.typeCreationFunctions = {};
resourceManager.typeDeletionFunctions = {};
resourceManager.typeInitialiseFunctions = {};
resourceManager.filesRequiredFunctions = {};
resourceManager.assetsRequiredList = {};
resourceManager.isLoaded = false;
resourceManager.registeredControllers = {};
resourceManager.controllerInstances = {};
resourceManager.plugins = {};
resourceManager.shared_plugins = {};
resourceManager.configValues = {};

resourceManager.loadProgressCallbacks = [];
resourceManager.loadCompleteCallbacks = [];

resourceManager.registerLoadProgressCallback = function (callback) {
  resourceManager.loadProgressCallbacks.push(callback);
};

resourceManager.registerLoadCompleteCallback = function (callback) {
  resourceManager.loadCompleteCallbacks.push(callback);
};

resourceManager.registerObjectType = function (
  name,
  creationFunction,
  initialiseFunction,
  fileRequiredFunction,
  deletionFunction
) {
  if (typeof resourceManager.typeCreationFunctions[name] !== "undefined") {
    // There is already an object of this type
    console.error("Object type " + name + " already registered");
    return;
  }

  if (typeof creationFunction === "undefined") {
    console.error("creation function not defined for object type " + name);
    return;
  }

  if (typeof initialiseFunction === "undefined") {
    console.error("initialise function not defined for object type " + name);
    return;
  }

  if (typeof fileRequiredFunction === "undefined") {
    // There was no function that returns the files required. This can be dealt with as a warning
    console.warn(
      "files required function() not defined for object type " + name
    );
    fileRequiredFunction = function () {
      return [];
    };
  }

  // Store the functions that create/delete the object, and list what files the object needs at load time
  resourceManager.typeCreationFunctions[name] = creationFunction;
  resourceManager.typeDeletionFunctions[name] = deletionFunction;
  resourceManager.typeInitialiseFunctions[name] = initialiseFunction;
  resourceManager.filesRequiredFunctions[name] = fileRequiredFunction;
};

resourceManager.initialise = function () {
  if (typeof ReflexLoader === "undefined") {
    console.log("resourceManager.initialise() - ReflexLoader not found");
    return;
  }

  if (typeof ReflexLoader.resourceData === "undefined") {
    console.log(
      "resourceManager.initialise() - ReflexLoader resource data not found"
    );
    return;
  }

  layoutController.initialise();

  ReflexLoader.loadFile(
    "config/config.json",
    resourceManager.createConfiguration
  );
};

// add files from textureLists into the preloadGraphics["mainGame"] array
// this is needed since moving away from texture packs and developers not
// needing to specify all the assets during the load section
// this will not catch assets used in dynamically created sprites
resourceManager.populatePreloadGraphicsFromTextureLists = function () {
  const textureLists = resourceManager.configuration.textureLists;
  const keys = Object.keys(textureLists);
  let fileNames = [];
  for (let k = 0; k < keys.length; k++) {
    const textureObject = textureLists[keys[k]];
    // this switch has been borrowed from textureParser.createTextures
    switch (textureObject.format) {
      case "list":
        {
          for (let i = 0; i < textureObject.files.length; i++) {
            const filename = gdk.ammendResourceName(
              ReflexLoader.rootResDir + textureObject.files[i]
            );
            if (
              fileNames.indexOf(filename) === -1 &&
              resourceManager.configuration.preloadGraphics["mainGame"].indexOf(
                filename
              ) === -1
            ) {
              fileNames.push(filename);
            }
          }
        }
        break;

      case "files":
        {
          const fileRootIndex =
            typeof textureObject.baseFileIndex === "undefined"
              ? 0
              : textureObject.baseFileIndex;
          const step =
            typeof textureObject.fileIndexStep === "undefined"
              ? 1
              : textureObject.fileIndexStep;

          for (let i = 0; i < textureObject.totalFrames; i++) {
            const tempFileName = sprintf(
              textureObject.filenameFormat,
              fileRootIndex + i * step
            );
            const filename = gdk.ammendResourceName(
              ReflexLoader.rootResDir + tempFileName
            );
            if (
              fileNames.indexOf(filename) === -1 &&
              resourceManager.configuration.preloadGraphics["mainGame"].indexOf(
                filename
              ) === -1
            ) {
              fileNames.push(filename);
            }
          }
        }
        break;

      default:
        {
        }
        break;
    }
  }

  resourceManager.configuration.preloadGraphics["mainGame"] =
    resourceManager.configuration.preloadGraphics["mainGame"].concat(fileNames);
};

resourceManager.compileAssetsRequired = function () {
  // before we do anything, lets use "textureLists": {
  // and populate preloadGraphics["mainGame"] with the file list.
  // duplicates will automatically be filtered out below
  // we need this now because we have turned of preCreateSprites as well
  // no longer loading texture packs. We need the normal loader to load assets to make
  // sure they are tagged with the correct defaultQueryString
  // this will not catch sprites being created dynamically in code
  resourceManager.populatePreloadGraphicsFromTextureLists();

  if (typeof resourceManager.configuration.loadGroups === "undefined") {
    console.error("Load groups were not defined in config.json");
  }

  // First check each container has a valid loadGroup defined
  for (var prop in resourceManager.layoutJSON) {
    if (resourceManager.layoutJSON.hasOwnProperty(prop)) {
      var loadGroup = resourceManager.layoutJSON[prop].loadGroup;
      if (loadGroup === "undefined") {
        console.error(
          "Container '" + prop + "' does not have a load group defined"
        );
        return;
      }

      if (resourceManager.configuration.loadGroups.indexOf(loadGroup) === -1) {
        console.error(
          "Load group '" +
            loadGroup +
            "' in Container '" +
            prop +
            "' was not found in config.json"
        );
        return;
      }
    }
  }

  // Create load group arrays, ready for populating with files required
  for (var i = 0; i < resourceManager.configuration.loadGroups.length; i++) {
    var loadGroup = resourceManager.configuration.loadGroups[i];
    resourceManager.assetsRequiredList[loadGroup] = [];
    if (
      resourceManager.configuration.preloadGraphics &&
      resourceManager.configuration.preloadGraphics[loadGroup]
    ) {
      if (
        Array.isArray(resourceManager.configuration.preloadGraphics[loadGroup])
      ) {
        resourceManager.assetsRequiredList[loadGroup] =
          resourceManager.configuration.preloadGraphics[loadGroup].slice(0);
      }
    }
  }

  // Compile a list of the assets we will need for each load group
  for (var i = 0; i < resourceManager.configuration.loadGroups.length; i++) {
    // Compile assets for the following load group
    var loadGroup = resourceManager.configuration.loadGroups[i];
    var assetsRequiredList = resourceManager.assetsRequiredList[loadGroup];

    for (var prop in resourceManager.layoutJSON) {
      if (resourceManager.layoutJSON.hasOwnProperty(prop)) {
        if (resourceManager.layoutJSON[prop].loadGroup === loadGroup) {
          // This container is part of this load group, so add assets
          var returnList = [];
          resourceManager.getAssetsRequiredInTree(
            resourceManager.layoutJSON[prop].children,
            returnList
          );

          // Only add non duplicates
          for (var j = 0; j < returnList.length; j++) {
            if (assetsRequiredList.indexOf(returnList[j]) === -1) {
              // Not added, so add
              assetsRequiredList.push(returnList[j]);
            }
          }
        }
      }
    }
  }

  // Calculate how many files we need to load in total
  resourceManager.totalFilesToLoad = 0;
  for (var prop in resourceManager.assetsRequiredList) {
    if (resourceManager.assetsRequiredList.hasOwnProperty(prop)) {
      resourceManager.totalFilesToLoad +=
        resourceManager.assetsRequiredList[prop].length;
    }
  }
  resourceManager.totalFilesToLoad += resourceManager.totalSoundsToLoad;
  resourceManager.remainingFilesToLoad = resourceManager.totalFilesToLoad;

  // Kick off the loading process
  resourceManager.currentLoadGroup = 0;
  resourceManager.beginNextLoadingGroup();
  gdk.loadingBegin();
};

resourceManager.getAssetsRequiredInTree = function (treeRoot, returnList) {
  for (var i = 0; i < treeRoot.length; i++) {
    var list = [];
    var type = treeRoot[i].type;

    // Check object type is registered
    if (typeof resourceManager.typeCreationFunctions[type] === "undefined") {
      console.error("Unregistered object type '" + type + "' in layout.json");
      return;
    }

    if (Reflex.developerMode) {
      // Here we check if getFileRequired is valid
      var filesRequiredFn = resourceManager.filesRequiredFunctions[type];
      if (typeof filesRequiredFn !== "function") {
        console.warn(
          "Object type " + name + " getFilesRequired is not a valid function"
        );
      } else {
        var files = filesRequiredFn(treeRoot[i]);

        if (typeof files !== "object") {
          console.error(
            "Object type " +
              type +
              " getFilesRequired did not return a valid object"
          );
        } else if (!Array.isArray(files)) {
          console.error(
            "Object type " + type + " getFilesRequired did not return an array"
          );
        } else if (files.length === 0) {
          // These are the library objects that do not require graphics
          var valid = [
            "container",
            "text",
            "graphics",
            "meter",
            "clock",
            "helpPageManager",
            "homeButton",
            "optionsButton",
            "settingsMenuButton",
            "stakeButton",
            "autoplayButton",
            "autoplayCancelButton",
            "spinButton",
            "slamStopButton",
            "skipWinsButton",
            "helpPageNextButton",
            "helpPagePrevButton",
            "helpPageCloseButton",
            "gridContainer",
            "winlineDiagram",
            "demoManager",
            "tabbedDemoManager",
            "payTable",
          ];
          if (valid.indexOf(type) === -1) {
            console.warn(
              "Object type " + type + " reports it does not require any files"
            );
          }
        }
      }
    }

    // Ask this object what files it will need
    var filesRequired = resourceManager.filesRequiredFunctions[type](
      treeRoot[i]
    );

    if (typeof filesRequired === "undefined") {
      console.error(
        "Files required function returned 'undefined' for object '" +
          treeRoot[i].ID +
          "'"
      );
      filesRequired = [];
    }

    // Add non duplicates
    for (var j = 0; j < filesRequired.length; j++) {
      if (typeof filesRequired[j] === "undefined") {
        console.error(
          "Files required function returned type '" +
            typeof filesRequired[j] +
            "' for object '" +
            treeRoot[i].ID +
            "', index " +
            j
        );
      } else {
        if (returnList.indexOf(filesRequired[j]) === -1) {
          returnList.push(filesRequired[j]);
        }
      }
    }

    if (typeof treeRoot[i].children !== "undefined") {
      resourceManager.getAssetsRequiredInTree(treeRoot[i].children, returnList);
    }
  }
};

resourceManager.beginNextLoadingGroup = function () {
  var loadGroup =
    resourceManager.configuration.loadGroups[resourceManager.currentLoadGroup];

  resourceManager.totalFilesToLoad -=
    resourceManager.assetsRequiredList[loadGroup].length;
  resourceManager.remainingFilesToLoad -=
    resourceManager.assetsRequiredList[loadGroup].length;

  var actualFilesRequired = ReflexLoader.startLoading(
    resourceManager.assetsRequiredList[loadGroup],
    loadGroup,
    resourceManager.loadProgress,
    resourceManager.loadComplete
  );

  resourceManager.totalFilesToLoad += actualFilesRequired;
  resourceManager.remainingFilesToLoad += actualFilesRequired;
};

resourceManager.evaluateCreateIf = function (evalString) {
  var splitIndex = evalString.split("=");
  var left = splitIndex[0];
  var right = splitIndex[splitIndex.length - 1];
  var lastLhsIndex = left.length - 1;

  switch (left.charAt(lastLhsIndex)) {
    case "!": //Check if this is a != operator
      return left.substr(0, lastLhsIndex) != right;
      break;
    case ">": //Check if this is a >= operator
      return left.substr(0, lastLhsIndex) >= right;
      break;
    case "<": //Check if this is a <= operator
      return left.substr(0, lastLhsIndex) <= right;
      break;
    default:
      break;
  }

  return left == right;
};
resourceManager.evaluateChildCreation = function (jsonData) {
  if (jsonData.create_if !== undefined) {
    if (!resourceManager.evaluateCreateIf(jsonData.create_if)) {
      return false;
    }
  }

  var children = jsonData.constructor === Array ? jsonData : jsonData.children;
  if (children !== undefined) {
    for (var i = 0; i < children.length; ++i) {
      if (!resourceManager.evaluateChildCreation(children[i])) {
        children.splice(i, 1);
        --i;
      }
    }
  }

  return true;
};

resourceManager.externalJsonFiles = [];
resourceManager.getExternalJsonFile = function (filename) {
  // Find an element that has the same filename as the one specified
  return resourceManager.externalJsonFiles.find(function (element) {
    return element.filename === filename;
  });
};

resourceManager.appendConfigValues = function (currentList, newList) {
  var configValueList = {};
  currentList = currentList || resourceManager.configValues;
  newList = newList || {};

  // Duplicate the current list
  var childConfigValueKeys = Object.keys(currentList);
  for (var i = 0; i < childConfigValueKeys.length; ++i) {
    configValueList[childConfigValueKeys[i]] =
      currentList[childConfigValueKeys[i]];
  }
  // Add the values from the new list to the current list
  childConfigValueKeys = Object.keys(newList);
  for (var k = 0; k < childConfigValueKeys.length; ++k) {
    configValueList[childConfigValueKeys[k]] = newList[childConfigValueKeys[k]];
  }

  return configValueList;
};

resourceManager.checkForExternalJson = function (filename, layoutJSON) {
  var keys = Object.keys(layoutJSON);
  for (var i = 0; i < keys.length; ++i) {
    // Check if there is a child array, if there isn't then the layoutJSON could be the child array
    var children = layoutJSON[keys[i]].children;
    if (children === undefined) {
      if (!!layoutJSON[keys[i]] && layoutJSON[keys[i]].constructor === Array) {
        children = layoutJSON[keys[i]];
      } else {
        continue;
      }
    }

    for (var j = 0; j < children.length; ++j) {
      if (children[j].type === "externalJson") {
        children[j].type = "container"; // Convert the externalJson object to a container

        var externFilename = "config/" + children[j].fileName;
        var externJson = resourceManager.getExternalJsonFile(externFilename);

        // Get the config values for this external JSON
        var configValueList = resourceManager.appendConfigValues(
          layoutJSON[keys[i]].configValues,
          children[j].configValues
        );

        var parentJson = resourceManager.getExternalJsonFile(filename);
        if (parentJson !== undefined) {
          configValueList = resourceManager.appendConfigValues(
            configValueList,
            parentJson.configValues
          );
        }

        // Store the data for the external JSON that needs to be loaded
        resourceManager.externalJsonFiles.push({
          filename: externFilename,
          layoutJSON:
            externJson !== undefined ? externJson.layoutJSON : undefined,
          childCount: 0,
          parentFilename: filename,
          parentJson: layoutJSON[keys[i]],
          index: j,
          configValues: configValueList,
        });

        // Update the parent data so that it knows how many child layouts need to be loaded before it can be correctly rebuilt
        var parentLayoutData = resourceManager.getExternalJsonFile(filename);
        if (parentLayoutData !== undefined) {
          ++parentLayoutData.childCount;
        }
      } else if (
        children[j].children !== undefined &&
        children[j].children.length > 0
      ) {
        resourceManager.checkForExternalJson(filename, children[j]);
      }
    }
  }
};

resourceManager.loadExternalJsonComplete = function (filename, data) {
  for (var i = 0; i < resourceManager.externalJsonFiles.length; ++i) {
    var externJson = resourceManager.externalJsonFiles[i];
    // Check if the loaded file is the one we want to use and that we haven't already parsed it
    if (
      externJson.filename === filename &&
      externJson.layoutJSON === undefined
    ) {
      // Replace the config values into the string
      var layoutData = String(data);
      var keys = Object.keys(externJson.configValues);
      for (var j = 0; j < keys.length; ++j) {
        var searchStr = ("%" + keys[j] + "%").replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        );
        if (
          Array.isArray(externJson.configValues[keys[j]]) ||
          typeof externJson.configValues[keys[j]] === "object"
        )
          layoutData = layoutData.replace(
            new RegExp(searchStr, "g"),
            JSON.stringify(externJson.configValues[keys[j]])
          );
        else
          layoutData = layoutData.replace(
            new RegExp(searchStr, "g"),
            externJson.configValues[keys[j]]
          );
      }

      // Parse the JSON into JS objects/array data
      externJson.layoutJSON = JSON.parse(layoutData);
      resourceManager.evaluateChildCreation(externJson.layoutJSON.external);
      // Check if this external JSON includes another
      resourceManager.checkForExternalJson(filename, externJson.layoutJSON);
    }
  }

  // Check for an unloaded external JSON file
  var nextJson = resourceManager.externalJsonFiles.find(function (element) {
    return element.layoutJSON === undefined;
  });

  if (nextJson === undefined) {
    // All JSON files have been loaded

    do {
      for (var i = 0; i < resourceManager.externalJsonFiles.length; ++i) {
        // Check if the JSON requires another one to be merged first
        var externJS = resourceManager.externalJsonFiles[i];
        if (externJS.childCount === 0) {
          // Merge this JSON in to the parents child list
          var externJSChild = (externJS.parentJson.children ||
            externJS.parentJson)[externJS.index];
          if (externJSChild.children === undefined) {
            externJSChild.children = [];
          }
          externJSChild.children = externJSChild.children.concat(
            externJS.layoutJSON.external
          );
          externJS.layoutJSON.external = [];

          // Update this JSON files parent
          var parentLayoutData = resourceManager.getExternalJsonFile(
            externJS.parentFilename
          );
          if (parentLayoutData !== undefined) {
            --parentLayoutData.childCount;
          }

          // This JSON has been merged, no need to process it further
          resourceManager.externalJsonFiles.splice(i, 1);
          --i;
        }
      }

      //Wait until all external JSON files have been merged back in to the main layoutJSON
    } while (resourceManager.externalJsonFiles.length > 0);

    resourceManager.compileAssetsRequired();
  } else {
    // Load the JSON file
    ReflexLoader.loadFile(
      nextJson.filename,
      resourceManager.loadExternalJsonComplete
    );
  }
};

resourceManager.loadLayoutComplete = function (filename, data) {
  // Replace the config values into the data string
  var layoutData = String(data);
  var keys = Object.keys(resourceManager.configValues);
  for (var i = 0; i < keys.length; ++i) {
    var searchStr = ("%" + keys[i] + "%").replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    layoutData = layoutData.replace(
      new RegExp(searchStr, "g"),
      resourceManager.configValues[keys[i]]
    );
  }

  // Now resource manager has the layout file, we need to start loading the graphics
  resourceManager.layoutJSON = JSON.parse(layoutData);
  var keys = Object.keys(resourceManager.layoutJSON);
  for (var i = 0; i < keys.length; ++i) {
    resourceManager.evaluateChildCreation(resourceManager.layoutJSON[keys[i]]);
  }
  resourceManager.checkForExternalJson(filename, resourceManager.layoutJSON);

  // There are no external JSON files, loading is complete
  if (resourceManager.externalJsonFiles.length === 0) {
    resourceManager.compileAssetsRequired();
  } else {
    // Check for an unloaded external JSON file
    var nextJson = resourceManager.externalJsonFiles.find(function (element) {
      return element.layoutJSON === undefined;
    });

    // Load the JSON file
    if (nextJson !== undefined) {
      ReflexLoader.loadFile(
        nextJson.filename,
        resourceManager.loadExternalJsonComplete
      );
    }
  }
};

resourceManager.startLoadLayout = function (filename) {
  // This loads the layout but does NOT create all the objects
  ReflexLoader.loadFile(filename, resourceManager.loadLayoutComplete);
};

resourceManager.hasResource = function (resourceName) {
  return (
    typeof resourceManager.generatedResources[resourceName] !== "undefined" &&
    resourceManager.generatedResources[resourceName]
  );
};
resourceManager.hasContainer = function (containerName) {
  return typeof resourceManager.containers[containerName] !== "undefined";
};
resourceManager.hasPlugin = function (pluginName) {
  return typeof resourceManager.plugins[pluginName] !== "undefined";
};

resourceManager.hasSharedPlugin = function (pluginName) {
  return typeof resourceManager.shared_plugins[pluginName] !== "undefined";
};

resourceManager.hasController = function (controllerName) {
  return (
    typeof resourceManager.controllerInstances[controllerName] !== "undefined"
  );
};

resourceManager.get = function (resourceName) {
  if (resourceManager.hasResource(resourceName)) {
    return resourceManager.generatedResources[resourceName].object;
  }

  console.log("resourceManager could not find resource " + resourceName);
  return undefined;
};

resourceManager.getContainer = function (containerName) {
  if (resourceManager.hasContainer(containerName)) {
    return resourceManager.containers[containerName];
  }

  console.log("resourceManager could not find container " + containerName);
  return undefined;
};

resourceManager.registerPlugIn = function (name, plugIn) {
  if (typeof resourceManager.plugins[name] !== "undefined") {
    console.log("Plugin " + name + " has already been registered");
    return;
  }
  resourceManager.plugins[name] = plugIn;
};

resourceManager.registerSharedPlugin = function (name, createFunction) {
  if (typeof resourceManager.shared_plugins[name] !== "undefined") {
    console.log("Shared Plugin " + name + " has already been registered");
    return;
  }
  resourceManager.shared_plugins[name] = {
    creator: createFunction,
    inst: null,
  };
};

resourceManager.getPlugin = function (pluginName) {
  if (resourceManager.hasPlugin(pluginName)) {
    return resourceManager.plugins[pluginName];
  }

  console.log("Unknown plugin - " + pluginName);
  return undefined;
};

resourceManager.getSharedPlugin = function (pluginName) {
  if (resourceManager.hasSharedPlugin(pluginName)) {
    let obj = resourceManager.shared_plugins[pluginName];
    if (!obj.inst) {
      // lazy eval
      obj.inst = obj.creator();
    }
    return resourceManager.shared_plugins[pluginName].inst;
  }

  console.log("Unknown plugin - " + pluginName);
  return undefined;
};

resourceManager.registerController = function (type, createFunction) {
  if (checkForDefined(resourceManager.registeredControllers[type])) {
    console.log("A controller of type '" + type + "' has already been created");
    return;
  }

  if (typeof createFunction !== "function") {
    console.error(
      "Registered creation function for controller " +
        type +
        " is not a function"
    );
    return;
  }

  // Function that creates an instance of this controller
  resourceManager.registeredControllers[type] = createFunction;
};

resourceManager.getController = function (name) {
  if (typeof resourceManager.controllerInstances[name] === "undefined") {
    console.log("Unknown controller '" + name + "'");
    return;
  }

  return resourceManager.controllerInstances[name];
};

resourceManager.getConfiguration = function () {
  if (typeof resourceManager.configuration === "undefined") {
    console.log(
      "Resource Manager cannot return configuration as it has not been loaded yet"
    );
  }
  return resourceManager.configuration;
};

resourceManager.getContainerPosition = function (containerName) {
  return Reflex.getContainerPosition(containerName).position;
};

resourceManager.getLogicalScreenSize = function () {
  return {
    width:
      resourceManager.configuration.layouts[layoutController.getCurrentLayout()]
        .width,
    height:
      resourceManager.configuration.layouts[layoutController.getCurrentLayout()]
        .height,
  };
};

resourceManager.getPhysicalScreenSize = function () {
  return {
    width: systemData.PHYSICAL_SCREEN_WIDTH,
    height: systemData.PHYSICAL_SCREEN_HEIGHT,
  };
};

resourceManager.loadParticleDefinition = function () {
  var file = resourceManager.particleEmmitterDefinitionsLoadList[0].fileName;
  ReflexLoader.loadFile(file, resourceManager.particleDefinitionLoaded);
};

resourceManager.particleDefinitionLoaded = function (filename, responseText) {
  resourceManager.particleEmmitterDefinitions[
    resourceManager.particleEmmitterDefinitionsLoadList[0].name
  ] = JSON.parse(responseText);
  resourceManager.particleEmmitterDefinitions[
    resourceManager.particleEmmitterDefinitionsLoadList[0].name
  ].scale.start *= resourceManager.selectedAssetResolution.scaleFactor;
  resourceManager.particleEmmitterDefinitions[
    resourceManager.particleEmmitterDefinitionsLoadList[0].name
  ].scale.end *= resourceManager.selectedAssetResolution.scaleFactor;
  resourceManager.particleEmmitterDefinitionsLoadList.splice(0, 1);

  if (resourceManager.particleEmmitterDefinitionsLoadList.length > 0) {
    // Start loading particle definitions
    resourceManager.loadParticleDefinition(
      resourceManager.particleEmmitterDefinitionsLoadList[0]
    );
  } else {
    // All particle definitions loaded, so not we can load the layout file
    resourceManager.startLoadLayout("config/layout.json");
  }
};

resourceManager.selectBestAssetResolution = function () {
  var assetResolutionsList = resourceManager.configuration.assetResolutions;

  if (typeof assetResolutionsList === "undefined") {
    console.error("config.json does not contain an 'assetResolutions' section");
    return;
  }

  var actualWH = Math.max(
    screen.width * window.devicePixelRatio,
    screen.height * window.devicePixelRatio
  );
  var maxResFound = 0;
  var maxResName = "";
  var selectedMinResFound = 99999;
  var selectedResName = "";

  for (var prop in assetResolutionsList) {
    if (typeof assetResolutionsList[prop] === "object") {
      var resolution = assetResolutionsList[prop];

      // If this is a mobile device, check if it is allowed
      var allowOnMobile =
        typeof resolution.allowOnMobile === "undefined"
          ? true
          : resolution.allowOnMobile;
      var valid = gdk.deviceInfo.mobile ? allowOnMobile : true;

      // In case the resolution of the device isnt covered, store the best res found
      if (resolution.maxWH > maxResFound && valid) {
        maxResFound = resolution.maxWH;
        maxResName = prop;
      }

      // Is this a vialble resolution?
      if (actualWH <= resolution.maxWH && valid) {
        // Yes so check min and max we have already found
        if (resolution.maxWH < selectedMinResFound) {
          // We found a better asset size
          selectedResName = prop;
          selectedMinResFound = resolution.maxWH;
        }
      }
    }
  }

  if (selectedResName === "") {
    // Screen is too large for our best resolution, so use the best we have
    selectedResName = maxResName;
  }

  // Now we have selected the best asset resolution, inform the loader to use this new diretory
  ReflexLoader.setGraphicsResDirectory(
    assetResolutionsList[selectedResName].subDirectory
  );
  return selectedResName;
};

resourceManager.getTextureListDefinition = function (name) {
  if (
    typeof resourceManager.configuration.textureLists[name].format ===
    "undefined"
  ) {
    // This texture list changes dependent on resolution
    return resourceManager.configuration.textureLists[name][
      resourceManager.selectedAssetResolution.name
    ];
  } else {
    // This texture list is for all resolutions
    return resourceManager.configuration.textureLists[name];
  }
};

resourceManager.createConfiguration = function (filename, configurationData) {
  // Create configuration
  resourceManager.configuration = JSON.parse(configurationData);

  if (typeof resourceManager.configuration.fontDefinitions === "undefined") {
    console.warn(
      "No font definitions were defined for this game in config.json"
    );
  }

  if (typeof resourceManager.configuration.layouts === "undefined") {
    // We need to create default layouts
    resourceManager.configuration.layouts = [
      {
        name: "landscape_default",
        width: 1920,
        height: 1080,
        landscape: true,
        mobile: false,
      },
      {
        name: "portrait_default",
        width: 1080,
        height: 1920,
        landscape: false,
        mobile: false,
      },
      {
        name: "landscape_mobile",
        width: 1920,
        height: 1080,
        landscape: true,
        mobile: true,
      },
      {
        name: "portrait_mobile",
        width: 1080,
        height: 1920,
        landscape: false,
        mobile: true,
      },
    ];
  } else {
    var keys = Object.keys(resourceManager.configuration.layouts);
    for (var i = 0; i < keys.length; i++) {
      var layout = resourceManager.configuration.layouts[keys[i]];
      if (layout.landscape === undefined) {
        layout.landscape = false;
      }
      if (layout.mobile === undefined) {
        layout.mobile = false;
      }
    }
  }

  // Choose an asset resolution
  var layoutName = resourceManager.selectBestAssetResolution();
  resourceManager.selectedAssetResolution =
    resourceManager.configuration.assetResolutions[layoutName];
  resourceManager.selectedAssetResolution.name = layoutName;
  resourceManager.selectedAssetResolution.scaleFactor =
    1 / resourceManager.selectedAssetResolution.scale;
  console.log("Selected Assets: " + layoutName);

  // Initialise textures lists
  if (typeof resourceManager.configuration.textureLists !== "undefined") {
    var textureList = resourceManager.configuration.textureLists;
    for (var subProp in textureList) {
      if (typeof textureList[subProp] === "object") {
        textureParser.addTextureList(
          subProp,
          resourceManager.getTextureListDefinition(subProp)
        );
      }
    }
  }

  // Add movie definition for static symbols
  resourceManager.configuration.movieDefinitions["staticSymbols"] = {
    textureList: "staticSymbols",
    fps: 1,
  };

  // Read in particle definitions
  resourceManager.particleEmmitterDefinitionsLoadList = [];
  resourceManager.particleEmmitterDefinitions = {};
  if (
    typeof resourceManager.configuration.particleEffectDefinitions !==
    "undefined"
  ) {
    var defList = resourceManager.configuration.particleEffectDefinitions;
    for (var subProp in defList) {
      if (typeof defList[subProp] === "object") {
        resourceManager.particleEmmitterDefinitionsLoadList.push({
          fileName: ReflexLoader.rootDir + defList[subProp].fileName,
          name: subProp,
        });
      }
    }
  }

  // Store config values
  gdk.addConfigValue("WhiteBoxed", "" + gdk.isGameWhiteBox());
  gdk.addConfigValue("isMobile", gdk.deviceInfo.mobile || false);
  gdk.addConfigValue("EngineSupplier", gdk.getEngineSupplier());
  gdk.addConfigValue("RegulatedMarket", gdk.getRegulatedMarket());
  gdk.addConfigValue("AutoplayForbidden", !gdk.getPlatformAllowsAutoplay());
  gdk.addConfigValue("GambleForbidden", !gdk.getPlatformAllowsGamble());
  gdk.addConfigValue("UpstakeForbidden", !gdk.getPlatformAllowsUpstake());
  gdk.addConfigValue("OperatorName", gdk.getOperatorName());
  gdk.addConfigValue("GameRulesForbidden", !gdk.getPlatformAllowsGameRules());

  let allConfigValues = gdk.getAllConfigValues();
  for (let prop in allConfigValues) {
    if (allConfigValues.hasOwnProperty(prop)) {
      resourceManager.configValues[prop] = allConfigValues[prop];
    }
  }

  // As we about to starting to load assets, kick off screen updates
  requestAnimationFrame(Reflex.animate);

  // Check for loading external fonts
  if (checkForDefined(resourceManager.configuration.externalFonts)) {
    if (!Array.isArray(resourceManager.configuration.externalFonts)) {
      console.error("externalsFonts in configuration is not a list");
      return;
    }

    if (resourceManager.configuration.externalFonts.length > 0) {
      // Start loading fonts
      resourceManager.startLoadingExternalFonts();
      return;
    }
  }

  resourceManager.startLoadingGraphics();
};

resourceManager.startLoadingExternalFonts = function () {
  if (typeof document.fonts === "undefined") {
    // No fonts to load
    resourceManager.startLoadingGraphics();
    return;
  }

  resourceManager.fontsLoadingRemaining = 0;

  for (var i = 0; i < resourceManager.configuration.externalFonts.length; i++) {
    resourceManager.fontsLoadingRemaining++;

    var fontDef = resourceManager.configuration.externalFonts[i];
    if (checkForUndefined(fontDef.fontFamily)) {
      console.error("fontFamily is undefined for external font " + i);
    } else if (checkForUndefined(fontDef.fileName)) {
      console.error("fileName is undefined for external font " + i);
    } else {
      if (typeof FontFace !== "undefined") {
        var fontDir =
          (gdk.serverParser.rootAssetPath || "") +
          ReflexLoader.rootDir +
          "Fonts/";
        let fontUrl = fontDir + fontDef.fileName;
        fontUrl = gdk.ammendResourceName(fontUrl);
        var newFontFace = new FontFace(
          fontDef.fontFamily,
          "url(" + fontUrl + ")"
        );

        newFontFace
          .load()
          .then(function (loaded_face) {
            // Font loaded OK
            document.fonts.add(loaded_face);
            resourceManager.onFontLoaded();
          })
          .catch(function (error) {
            // Error loading font
            resourceManager.onFontLoaded();
          });
      } else {
      }
    }
  }
};

resourceManager.onFontLoaded = function () {
  if (--resourceManager.fontsLoadingRemaining === 0) {
    for (
      var i = 0;
      i < resourceManager.configuration.externalFonts.length;
      i++
    ) {
      var fontDef = resourceManager.configuration.externalFonts[i];
      var text = new PIXI.Text("", {
        fontFamily: fontDef.fontFamily,
        fontSize: 10,
        fill: "#FFFFFF",
      });
      systemData.stage.addChild(text);
    }

    resourceManager.startLoadingGraphics();
  }
};

resourceManager.startLoadingGraphics = function () {
  // Now we know have a configuration, load all atlas files
  var atlasRelativeList = [];
  var atlasFileList = resourceManager.configuration.atlasFiles;
  if (checkForUndefined(atlasFileList)) {
    resourceManager.startSoundManagerLoadConfig();
  } else {
    var atlasFileList =
      resourceManager.configuration.atlasFiles[
        resourceManager.selectedAssetResolution.name
      ];

    for (var i = 0; i < atlasFileList.length; i++) {
      atlasRelativeList.push(ReflexLoader.rootResDir + atlasFileList[i]);
    }

    ReflexLoader.loadAtlasDataFiles(
      atlasRelativeList,
      resourceManager.startSoundManagerLoadConfig
    );
  }
};

resourceManager.startSoundManagerLoadConfig = function () {
  soundManager.loadConfig(resourceManager.onSoundConfigLoaded);
};

resourceManager.createLayoutObjects = function (loadGroupName, resources) {
  if (loadGroupName !== "loading") {
    //return;
  }

  var json;
  var layoutJSON = {};

  // Create all required containers, and compile what JSON is in this load group
  for (var prop in resourceManager.layoutJSON) {
    if (resourceManager.layoutJSON.hasOwnProperty(prop)) {
      if (typeof resourceManager.containers[prop] === "undefined") {
        resourceManager.containers[prop] = new PIXI.Container();
        resourceManager.containers[prop].Props = {};
        resourceManager.containers[prop].Props.ID = prop;
        resourceManager.containers[prop].visible =
          resourceManager.layoutJSON[prop].loadGroup ===
          resourceManager.configuration.loadGroups[0];
        for (var subProp in resourceManager.layoutJSON[prop]) {
          if (typeof resourceManager.layoutJSON[prop][subProp] != "object") {
            resourceManager.containers[prop].Props[subProp] =
              resourceManager.layoutJSON[prop][subProp];
          }
        }

        switch (resourceManager.containers[prop].Props.rootContainer) {
          case "recovery":
            {
              systemData.recoveryContainer.addChild(
                resourceManager.containers[prop]
              );
              resourceManager.containers[prop].visible = true;
            }
            break;

          default:
            {
              systemData.gameContainer.addChild(
                resourceManager.containers[prop]
              );
            }
            break;
        }
      }

      // Load all containers in this load group
      if (resourceManager.containers[prop].Props.loadGroup === loadGroupName) {
        layoutJSON[prop] = resourceManager.layoutJSON[prop];
      }
    }
  }

  // Tell layout controller to create objects
  layoutController.createLayoutObjects(layoutJSON, resources);

  // Set up initial logical coordinates
  var layout =
    resourceManager.configuration.layouts[layoutController.getCurrentLayout()];
  Reflex.setLogicalCoordinates(layout.width, layout.height);

  // Set up view port etc
  Reflex.updateViewport();

  // Now layout has loaded, and objects have been created, do initial layout
  layoutController.updateLayout();
};

resourceManager.loadProgress = function (loadGroup, resourceLoaded, resources) {
  //var percent = progress / 100;
  resourceManager.remainingFilesToLoad--;

  // Count from 0 to 95% else it looks like it sits at 100% when loading sound
  // NOTE: When loading some resources, PIXI will automagically add in resources which we don't track
  // This can sometimes cause progress to go over 100%, so we cap this to 95%
  var percent = Math.min(
    0.95,
    (1 -
      resourceManager.remainingFilesToLoad / resourceManager.totalFilesToLoad) *
      0.95
  );

  gdk.loadingProgress(percent);

  // Tell game we have loaded and initialised everything
  for (var i = 0; i < resourceManager.loadProgressCallbacks.length; i++) {
    resourceManager.loadProgressCallbacks[i](
      percent,
      loadGroup,
      resourceLoaded,
      resources
    );
  }
};

resourceManager.loadComplete = function (loadGroup, resources) {
  // Now we have loaded and pre create objects, create actual layout objects
  resourceManager.createLayoutObjects(loadGroup, resources);

  // Tell game we have loaded this load group
  for (let i = 0; i < resourceManager.loadCompleteCallbacks.length; i++) {
    resourceManager.loadCompleteCallbacks[i](loadGroup, resources);
  }

  // Have we finished loading all call groups?
  if (
    ++resourceManager.currentLoadGroup <
    resourceManager.configuration.loadGroups.length
  ) {
    // No , so kick off next load group
    resourceManager.beginNextLoadingGroup();
  } else {
    // now before we look to see if we need to load any audio
    // have a look to see if we are a replay game
    if (Reflex.replayGame === true) {
      resourceManager.onSoundLoadingComplete();
    } else {
      // Now all graphics are loaded, do we need to load sound?
      if (resourceManager.totalSoundsToLoad > 0) {
        // Yes, so start loading all sounds
        soundManager.loadFiles(
          resourceManager.loadProgress,
          resourceManager.onSoundLoadingComplete
        );
      } else {
        // No, so sound loading is complete
        resourceManager.onSoundLoadingComplete();
      }
    }
  }
};

resourceManager.onSoundConfigLoaded = function (soundsToLoad) {
  resourceManager.totalSoundsToLoad = soundsToLoad;

  // Now sound config has loaded, load particle definitions
  if (resourceManager.particleEmmitterDefinitionsLoadList.length > 0) {
    // Start loading particle definitions
    resourceManager.loadParticleDefinition();
  } else {
    // There are no particle definitions to read in, so start loading each layout
    resourceManager.startLoadLayout("config/layout.json");
  }
};

resourceManager.onSoundLoadingComplete = function () {
  // Tell game we have loaded sounds
  for (var i = 0; i < resourceManager.loadProgressCallbacks.length; i++) {
    resourceManager.loadProgressCallbacks[i](1, "sounds", {}, {});
  }

  for (var i = 0; i < resourceManager.loadCompleteCallbacks.length; i++) {
    resourceManager.loadCompleteCallbacks[i]("sounds", {});
  }

  //resourceManager.onEverythingLoaded();
  resourceManager.preCreateSprites();
};

resourceManager.preCreateSprites = function () {
  let doPreCreate =
    resourceManager.getConfiguration().preCreateSprites !== undefined
      ? resourceManager.getConfiguration().preCreateSprites
      : true;
  if (!doPreCreate) {
    resourceManager.onLoadComplete();
    return;
  }

  var movie;
  var keys = Object.keys(resourceManager.configuration.textureLists);
  resourceManager.preCreatedSpritesContainer = new PIXI.Container();
  systemData.gameContainer.addChild(resourceManager.preCreatedSpritesContainer);

  for (var i = 0; i < keys.length; i++) {
    var textures = textureParser.getClonedTextureList(keys[i]);
    for (var j = 0; j < textures.length; j++) {
      var sprite = new PIXI.Sprite(textures[j]);
      sprite.width = 1;
      sprite.height = 1;
      sprite.alpha = 0.01;
      resourceManager.preCreatedSpritesContainer.addChild(sprite);
    }
  }

  // Set up a check for all textures loaded
  window.setTimeout(resourceManager.checkPreCreateSprites, 100);
};

resourceManager.checkPreCreateSprites = function () {
  for (
    var i = resourceManager.preCreatedSpritesContainer.children.length - 1;
    i >= 0;
    i--
  ) {
    if (
      resourceManager.preCreatedSpritesContainer.children[i].texture.baseTexture
        .hasLoaded
    ) {
      resourceManager.preCreatedSpritesContainer.removeChild(
        resourceManager.preCreatedSpritesContainer.children[i]
      );
    }
  }

  if (resourceManager.preCreatedSpritesContainer.children.length === 0) {
    // Remove temporary container
    systemData.gameContainer.removeChild(
      resourceManager.preCreatedSpritesContainer
    );
    resourceManager.preCreatedSpritesContainer = undefined;

    // Completed pre creating movies
    resourceManager.onLoadComplete();
  } else {
    // Not all textures have finished loading, so reschedule another check
    window.setTimeout(resourceManager.checkPreCreateSprites, 100);
  }
};

resourceManager.onLoadComplete = function (delayInMS) {
  const duration =
    typeof delayInMS === "number" && delayInMS > 0 ? delayInMS : 1000;
  const tag = toString.call(Loader.setLoadProgress);
  const isFunc =
    tag === "[object Function]" || tag === "[object AsyncFunction]";
  if (isFunc) {
    Loader.setLoadProgress(1);
  } else {
    if (Loader?.loadMask?.scale) {
      Loader.loadMask.scale.x = 1;
    } else if (Loader?.progressBar?.scale) {
      Loader.progressBar.scale.x = 1;
    }
  }
  window.setTimeout(() => {
    for (let i = 0; i < resourceManager.loadCompleteCallbacks.length; i++) {
      resourceManager.loadCompleteCallbacks[i]("Creating Resources");
    }
    // Art and sounds have now been loaded, and we have pre created movies
    resourceManager.onEverythingLoaded();
  }, duration);
};

resourceManager.onPreCreatedMovieLoaded = function (movie) {
  // Get rid of movie
  this.parent.removeChild(this);
  resourceManager.loadProgress("Creating Resources");
};

resourceManager.onEverythingLoaded = function () {
  // Go through all controllers we need to create from the config
  for (var i = 0; i < resourceManager.configuration.controllers.length; i++) {
    var controller = resourceManager.configuration.controllers[i];
    if (checkForUndefined(controller)) {
      console.log("Controller not an object");
      return;
    }

    let success = resourceManager.createController(
      controller.ID,
      controller.type,
      controller
    );
    if (!success) {
      console.log("Error creating controller at index: " + i);
    }
  }

  // Game ready to start
  resourceManager.isLoaded = true;
  Reflex.onReady();
  gdk.loadingComplete();
};

resourceManager.createController = function (uniqueName, controllerType, json) {
  if (checkForUndefined(uniqueName)) {
    console.error("Controller does not have an ID defined");
    return false;
  }

  if (checkForDefined(resourceManager.controllerInstances[uniqueName])) {
    console.error(
      "Controller with ID " + uniqueName + " has already been created"
    );
    return false;
  }

  if (checkForUndefined(controllerType)) {
    console.error(
      "Controller '" + uniqueName + "' does not have a type defined"
    );
    return false;
  }

  // Check we have a definition for this controller
  if (
    checkForUndefined(resourceManager.registeredControllers[controllerType])
  ) {
    console.error(
      "Controller '" +
        uniqueName +
        "' type '" +
        controllerType +
        " is not registered"
    );
    return false;
  }

  // Create an instance of this controller and initialise
  resourceManager.controllerInstances[uniqueName] =
    resourceManager.registeredControllers[controllerType]();
  resourceManager.controllerInstances[uniqueName].Props = json;
  resourceManager.controllerInstances[uniqueName].name = uniqueName;
  resourceManager.controllerInstances[uniqueName].initialise(json);
  return true;
};

resourceManager.getMovieFPS = function (name) {
  // FPS can be either a number, or a list of fps for each asset resolution
  var movieDefinition =
    resourceManager.getConfiguration().movieDefinitions[name];

  if (typeof movieDefinition.fps === "object") {
    return movieDefinition.fps[resourceManager.selectedAssetResolution.name];
  } else {
    return movieDefinition.fps;
  }
};

resourceManager.resetObject = function (ID) {
  if (typeof ID === "object" && ID.name) {
    // If developer has passed in object rather than ID, use it's ID
    ID = ID.name;
  }

  // This function resets object via layoutController as if layout had been updated for this object
  if (!resourceManager.hasResource(ID)) {
    // Incorrect ID passed in
    console.error("Incorrect ID '" + ID + "' in resetObject");
    return;
  }

  var resource = resourceManager.generatedResources[ID];
  layoutController.updateObjectLayout(
    resource,
    resource.params[layoutController.getCurrentLayout()],
    resourceManager.selectedAssetResolution.scaleFactor
  );

  if (resource.object.children.length > 0) {
    for (var i = 0; i < resource.object.children.length; i++) {
      if (resource.object.children[i].name) {
        resourceManager.resetObject(resource.object.children[i].name);
      }
    }
  }
};

resourceManager.createObject = function (type, ID, parentName, json) {
  // This function is used to create objects on the fly
  // NOTE: This only creates a single object, and will not create tree if defined in JSON

  // If dev hasnt passed a json object, create one
  if (typeof json === "undefined") {
    json = { ID: ID, type: type };
  }

  // Check json has required data
  if (typeof json.ID === "undefined") {
    json.ID = ID;
  }

  if (typeof json.type === "undefined") {
    json.type = type;
  }

  if (typeof json.scale === "undefined") {
    json.scale = 1;
  }

  if (typeof json.scaleX === "undefined") {
    json.scaleX = json.scale;
  }

  if (typeof json.scaleY === "undefined") {
    json.scaleY = json.scale;
  }

  if (typeof json.x === "undefined") {
    json.x = 0;
  }

  if (typeof json.y === "undefined") {
    json.y = 0;
  }

  // We have a simple x/y/scale so create the structure to support this
  var keys = Object.keys(resourceManager.configuration.layouts);
  for (var i = 0; i < keys.length; i++) {
    if (checkForUndefined(json[keys[i]])) {
      json[keys[i]] = {
        position: { x: json.x, y: json.y },
        scaleX: json.scaleX,
        scaleY: json.scaleY,
      };
    }
    var jsonKey = json[keys[i]];

    if (checkForUndefined(jsonKey.position)) jsonKey.position = {};
    if (checkForDefined(json.x) && checkForUndefined(jsonKey.position.x))
      jsonKey.position.x = json.x;
    if (checkForDefined(json.y) && checkForUndefined(jsonKey.position.y))
      jsonKey.position.y = json.y;
    if (checkForDefined(json.scaleX) && checkForUndefined(jsonKey.scaleX))
      jsonKey.scaleX = json.scaleX;
    if (checkForDefined(json.scaleY) && checkForUndefined(jsonKey.scaleY))
      jsonKey.scaleY = json.scaleY;
    if (checkForDefined(json.alpha) && checkForUndefined(jsonKey.alpha))
      jsonKey.alpha = json.alpha;
    if (checkForDefined(json.rotation) && checkForUndefined(jsonKey.rotation))
      jsonKey.rotation = json.rotation;
  }

  if (typeof parentName === "object" && checkForDefined(parentName.name)) {
    // Developer passed the object rather than the parent name, so use parent name
    parentName = parentName.name;
  }

  // Find parent object
  var parentObject = resourceManager.generatedResources[parentName];
  if (typeof parentObject === "undefined") {
    // This may be a resource container
    parentObject = resourceManager.containers[parentName];
  }

  // Check we have a valid parent
  if (typeof parentObject === "undefined") {
    // We could not find parent object
    console.log(
      "Could not find " +
        parentName +
        " when trying to create object " +
        ID +
        " (" +
        type +
        ")"
    );
    console.log(
      "Note that objects cannot be created inside their parents create. Do this inside the initialise call"
    );
  }

  var newObject = layoutController.create(
    json,
    parentObject.object,
    parentName,
    PIXI.utils.TextureCache
  );

  // As we have added an object, a layout update will be required
  //layoutController.updateRequired = true;

  return newObject;
};

resourceManager.destroyObject = function (ID, destroyBaseTexture) {
  var resource = resourceManager.generatedResources[ID];
  if (checkForUndefined(resource)) {
    console.log(
      "Cannot delete object " +
        ID +
        " as it was not found in generated resources"
    );
    return;
  }

  for (var i = resource.object.children.length - 1; i >= 0; i--) {
    if (checkForDefined(resource.object.children[i].Props)) {
      resourceManager.destroyObject(resource.object.children[i].name);
    }
  }

  // Remove child in PIXI
  resource.object.parent.removeChild(resource.object);

  // Delete object from resource manager
  if (resourceManager.typeDeletionFunctions[resource.params.type] !== undefined)
    resourceManager.typeDeletionFunctions[resource.params.type](
      resource.object
    );

  delete resourceManager.generatedResources[ID];
};

resourceManager.setParent = function (obj, parentObj, updatePIXI) {
  if (obj === undefined || parentObj === undefined) {
    return;
  }

  var objName = obj.name;
  if (!resourceManager.hasResource(objName)) {
    var remaining = obj.children.length;
    for (var i = 0; i < remaining; ++i) {
      this.setParent(obj.children[i], parentObj, false);
    }
    return;
  }
  var parentObjName = parentObj.name;
  var newFQN = parentObjName + ":" + obj.Props.ID;
  var oldParentID = obj.Props.parentID;
  updatePIXI = updatePIXI === undefined ? true : updatePIXI;

  if (oldParentID === parentObjName) {
    return;
  }

  // Add to new ID
  resourceManager.generatedResources[newFQN] =
    resourceManager.generatedResources[objName];

  // Update links
  obj.name = newFQN;
  obj.Props.FQN = newFQN;
  obj.Props.parentID = parentObjName;

  // Need to update all child names/links as well
  var remaining =
    resourceManager.generatedResources[obj.name].object.children.length;
  for (var i = 0; i < remaining; ++i) {
    this.setParent(
      resourceManager.generatedResources[obj.name].object.children[i],
      obj,
      false
    );
  }

  if (updatePIXI) {
    // Add to new PIXI parent
    resourceManager.generatedResources[parentObj.Props.FQN].object.addChild(
      resourceManager.generatedResources[obj.name].object
    );

    // Update the objects transform if there is one
    if (
      resourceManager.generatedResources[obj.name].object.updateTransform !==
      undefined
    ) {
      resourceManager.generatedResources[obj.name].object.updateTransform();
    }
  }

  // Delete old ID
  resourceManager.generatedResources[objName] = undefined; // MB: Has to remove deletion as it was the issue that was causing iOS devices to crash. It appears it doesnt like the constant deletion and readding to the map coming from reels getNextSymbolFromInjector
};
